import { Alert, Checkbox, FormControlLabel, MenuItem, TextField } from '@mui/material'
import { FormEvent, useRef, useState } from 'react'
import {
  ApplicationRecord,
  DriverOnboarding,
  Employment,
  OnboardingChoices,
  saveDriverOnboarding,
} from 'shared/api/driverOnboarding'
import { Button } from 'shared/components/Button/Button'
import { SSNInput } from 'shared/components/MaskedInput/SSNInput'
import { TextV2 } from 'shared/components/Text/TextV2'
import { getDropdownStates } from 'shared/helpers/states'
import { createToken } from 'shared/models/basisTheory'
import { TYPOGRAPHY } from 'shared/theme/typographyV2'

import { isReviewSubmission } from './onboardingSubmission'
import { useOnboardingDraftState } from './useOnboardingDraftState'

export function StateField({
  label,
  value,
  onChange,
  required = false,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  required?: boolean
}) {
  return (
    <TextField
      select
      label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      required={required}
      fullWidth
    >
      <MenuItem value="">Choose a state</MenuItem>
      {getDropdownStates().map((state) => (
        <MenuItem key={state.value} value={state.value}>
          {state.label}
        </MenuItem>
      ))}
    </TextField>
  )
}

const emptyDetails: DriverOnboarding['details'] = {
  first_name: '',
  middle_name: '',
  last_name: '',
  email: '',
  mobile: '',
  dob: null,
  address: { line1: '', line2: '', city: '', state: '', zip: '' },
}
const emptyCdl: DriverOnboarding['cdl'] = {
  license_number: '',
  license_state: '',
  license_issued: null,
  license_expiration: null,
  has_ssn: false,
}

const emptyEmployment: Employment = {
  job_type: 'driving',
  start_date: '',
  end_date: null,
  company_name: '',
  company_address: '',
  position_held: '',
  can_contact_employer: false,
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  subject_to_fmcsr_regulations: false,
  subject_to_drug_and_alcohol_testing: false,
  leaving_reason_detail: '',
}

type FormProps = {
  record: DriverOnboarding
  choices: OnboardingChoices
  run: (action: () => Promise<DriverOnboarding>) => Promise<boolean>
  busy: boolean
}

export function DriverApplicationForm({
  record,
  save,
  busy,
  driverView = false,
}: {
  record: ApplicationRecord
  save: (kind: string, data: unknown, submit?: boolean) => Promise<boolean>
  busy: boolean
  driverView?: boolean
}) {
  const draft = (kind: string) => record.steps.find((step) => step.kind === kind)?.draft
  const visible = (kind: string) =>
    !driverView || record.steps.some((step) => step.kind === kind && step.assigned_at)
  const finished = (kind: string) =>
    driverView && !!record.steps.find((step) => step.kind === kind)?.assignment_completed_at
  const buttons = (kind: string, label: string) => (
    <div className="mt-3 flex flex-wrap gap-3">
      <Button type="submit" isDisabled={busy || finished(kind)} name="intent" value="save">
        Save {label}
      </Button>
      {driverView && (
        <Button
          type="submit"
          variant="secondary"
          ariaLabel={`Send ${label} for review`}
          isDisabled={busy || finished(kind)}
          name="intent"
          value="review"
        >
          Send for review
        </Button>
      )}
    </div>
  )
  const [details, setDetails] = useOnboardingDraftState(
    {
      ...(record.details || emptyDetails),
      ...draft('DETAILS'),
      address: {
        ...(record.details?.address || emptyDetails.address),
        ...(draft('DETAILS') as any)?.address,
      },
    },
    draft('DETAILS')
  )
  const [cdl, setCdl] = useOnboardingDraftState(
    { ...(record.cdl || emptyCdl), ...draft('CDL') },
    draft('CDL')
  )
  const [ssn, setSsn] = useState('')
  const [tokenError, setTokenError] = useState('')
  const [tokenizing, setTokenizing] = useState(false)
  const tokenPending = useRef(false)
  const [employment, setEmployment] = useOnboardingDraftState(
    (draft('EMPLOYMENT_HISTORY') as Employment[]) || record.employment || [],
    draft('EMPLOYMENT_HISTORY')
  )
  const [workState, setWorkState] = useOnboardingDraftState(
    (draft('DETAILS') as any)?.work_location_state ?? record.work_location_state ?? '',
    draft('DETAILS')
  )
  const locked = record.status === 'ELIGIBLE' || !!record.intake_locked
  const grid = 'grid grid-cols-2 gap-4 sm:grid-cols-1'
  const title = (text: string) => (
    <TextV2 type={TYPOGRAPHY.header3} role="heading" aria-level={3} className="mb-4">
      {text}
    </TextV2>
  )
  async function saveDetails(event: FormEvent) {
    event.preventDefault()
    await save('DETAILS', { ...details, work_location_state: workState }, isReviewSubmission(event))
  }

  async function saveCdl(event: FormEvent) {
    event.preventDefault()
    if (tokenPending.current) return
    tokenPending.current = true
    setTokenizing(true)
    const forReview = isReviewSubmission(event)
    let success = false
    try {
      const { has_ssn: _hasSsn, ...values } = cdl
      const ssnToken = ssn ? await createToken(ssn.replace(/\D/g, '')) : undefined
      success = await save('CDL', { ...values, ...(ssnToken ? { ssn_token: ssnToken } : {}) }, forReview)
    } catch {
      setTokenError('Unable to securely save the SSN. Please retry.')
    } finally {
      tokenPending.current = false
      setTokenizing(false)
    }
    if (success) {
      if (ssn) setCdl((value) => ({ ...value, has_ssn: true }))
      setSsn('')
      setTokenError('')
    }
  }
  const updateJob = (index: number, value: Partial<Employment>) =>
    setEmployment((jobs) => jobs.map((job, i) => (i === index ? { ...job, ...value } : job)))
  return (
    <div className="space-y-5">
      {locked && (
        <Alert severity="info">
          Application information is locked once consent is opened or screening is requested. The checklist
          retains the driver’s submitted information.
        </Alert>
      )}
      {visible('DETAILS') && (
        <form onSubmit={saveDetails} className="rounded-xl border border-gray-200 bg-white p-5">
          {title('1. Driver details')}
          <fieldset disabled={locked || busy || finished('DETAILS')} className="m-0 min-w-0 border-0 p-0">
            <div className={grid}>
              {(
                [
                  ['first_name', 'First name'],
                  ['middle_name', 'Middle name'],
                  ['last_name', 'Last name'],
                  ['email', 'Email'],
                  ['mobile', 'Mobile number'],
                ] as const
              ).map(([key, label]) => (
                <TextField
                  key={key}
                  label={label}
                  value={details[key]}
                  type={key === 'email' ? 'email' : key === 'mobile' ? 'tel' : 'text'}
                  onChange={(event) => setDetails({ ...details, [key]: event.target.value })}
                />
              ))}
              <TextField
                label="Date of birth"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={details.dob || ''}
                onChange={(event) => setDetails({ ...details, dob: event.target.value || null })}
              />
              {(
                [
                  ['line1', 'Home street address'],
                  ['line2', 'Apartment / suite'],
                  ['city', 'City'],
                  ['zip', 'ZIP code'],
                ] as const
              ).map(([key, label]) => (
                <TextField
                  key={key}
                  label={label}
                  value={details.address[key]}
                  onChange={(event) =>
                    setDetails({ ...details, address: { ...details.address, [key]: event.target.value } })
                  }
                />
              ))}
              <StateField
                label="Home state"
                value={details.address.state}
                onChange={(state) => setDetails({ ...details, address: { ...details.address, state } })}
              />
              <StateField
                label="Work location state for screening"
                value={workState}
                onChange={setWorkState}
              />
            </div>
            <TextV2 type={TYPOGRAPHY.paragraph2} className="text-mid-gray my-3">
              Save as much as you have now and return later. Your admin reviews saved information before it is
              used for screening.
            </TextV2>
            {!locked && buttons('DETAILS', 'driver details')}
          </fieldset>
        </form>
      )}
      {visible('EMPLOYMENT_HISTORY') && (
        <form
          onSubmit={(event) => {
            event.preventDefault()
            void save(
              'EMPLOYMENT_HISTORY',
              employment.map((job) => ({ ...job, start_date: job.start_date || null })),
              isReviewSubmission(event)
            )
          }}
          className="rounded-xl border border-gray-200 bg-white p-5"
        >
          {title('2. Employment history')}
          <fieldset
            disabled={locked || busy || finished('EMPLOYMENT_HISTORY')}
            className="m-0 min-w-0 space-y-5 border-0 p-0"
          >
            {employment.map((job, index) => (
              <div key={index} className="space-y-4 rounded-lg border border-gray-200 p-4">
                <TextV2 type={TYPOGRAPHY.label1}>Employer {index + 1}</TextV2>
                <div className={grid}>
                  <TextField
                    select
                    label="Work type"
                    value={job.job_type}
                    onChange={(event) =>
                      updateJob(index, { job_type: event.target.value as Employment['job_type'] })
                    }
                  >
                    <MenuItem value="driving">Class 8 tractor/trailer driving</MenuItem>
                    <MenuItem value="not_driving">Other employment</MenuItem>
                  </TextField>
                  <TextField
                    label="Employer name"
                    value={job.company_name}
                    onChange={(event) => updateJob(index, { company_name: event.target.value })}
                  />
                  <TextField
                    label="Employer city, state"
                    placeholder="Dallas, TX"
                    value={job.company_address}
                    onChange={(event) => updateJob(index, { company_address: event.target.value })}
                  />
                  <TextField
                    label="Position held"
                    value={job.position_held}
                    onChange={(event) => updateJob(index, { position_held: event.target.value })}
                  />
                  <TextField
                    label="Start month"
                    type="month"
                    InputLabelProps={{ shrink: true }}
                    value={job.start_date?.slice(0, 7) || ''}
                    onChange={(event) =>
                      updateJob(index, { start_date: event.target.value ? `${event.target.value}-01` : '' })
                    }
                  />
                  <TextField
                    label="End month (blank if current)"
                    type="month"
                    InputLabelProps={{ shrink: true }}
                    value={job.end_date?.slice(0, 7) || ''}
                    onChange={(event) =>
                      updateJob(index, { end_date: event.target.value ? `${event.target.value}-01` : null })
                    }
                  />
                  {(
                    [
                      ['contact_name', 'Contact name'],
                      ['contact_email', 'Contact email'],
                      ['contact_phone', 'Contact phone'],
                      ['leaving_reason_detail', 'Reason for leaving'],
                    ] as const
                  ).map(([key, label]) => (
                    <TextField
                      key={key}
                      label={label}
                      value={job[key]}
                      type={key === 'contact_email' ? 'email' : 'text'}
                      onChange={(event) => updateJob(index, { [key]: event.target.value })}
                    />
                  ))}
                </div>
                {(
                  [
                    ['can_contact_employer', 'May contact this employer'],
                    ['subject_to_fmcsr_regulations', 'Subject to FMCSR regulations'],
                    ['subject_to_drug_and_alcohol_testing', 'Subject to drug and alcohol testing'],
                  ] as const
                ).map(([key, label]) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        checked={job[key]}
                        onChange={(event) => updateJob(index, { [key]: event.target.checked })}
                      />
                    }
                    label={label}
                  />
                ))}
                {!locked && (
                  <Button
                    variant="secondary"
                    isDisabled={busy}
                    onClick={() => setEmployment((jobs) => jobs.filter((_, i) => i !== index))}
                  >
                    Remove employer {index + 1}
                  </Button>
                )}
              </div>
            ))}
            {!employment.length && (
              <TextV2 type={TYPOGRAPHY.paragraph2}>
                No employment history entered. Add employers, or record that the carrier holds this history
                using the checklist bypass.
              </TextV2>
            )}
            {!locked && (
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="secondary"
                  isDisabled={busy}
                  onClick={() => setEmployment((jobs) => [...jobs, { ...emptyEmployment }])}
                >
                  Add employer
                </Button>
                {buttons('EMPLOYMENT_HISTORY', 'employment history')}
              </div>
            )}
          </fieldset>
        </form>
      )}
      {visible('CDL') && (
        <form onSubmit={saveCdl} className="rounded-xl border border-gray-200 bg-white p-5">
          {title('3. CDL information')}
          <fieldset
            disabled={locked || busy || tokenizing || finished('CDL')}
            className="m-0 min-w-0 border-0 p-0"
          >
            <div className={grid}>
              <TextField
                label="CDL number"
                value={cdl.license_number}
                inputProps={{ maxLength: 20 }}
                onChange={(event) => setCdl({ ...cdl, license_number: event.target.value })}
              />
              <StateField
                label="CDL state"
                value={cdl.license_state}
                onChange={(license_state) => setCdl({ ...cdl, license_state })}
              />
              <TextField
                label="CDL issue date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={cdl.license_issued || ''}
                onChange={(event) => setCdl({ ...cdl, license_issued: event.target.value || null })}
              />
              <TextField
                label="CDL expiration date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={cdl.license_expiration || ''}
                onChange={(event) => setCdl({ ...cdl, license_expiration: event.target.value || null })}
              />
              {!locked && (
                <SSNInput
                  name="onboarding-ssn"
                  label={cdl.has_ssn ? 'Replace SSN (optional)' : 'SSN for screening'}
                  type="password"
                  value={ssn}
                  onChange={(event?: React.ChangeEvent<HTMLInputElement>) => {
                    if (event) setSsn(event.target.value)
                  }}
                />
              )}
            </div>
            <TextV2 type={TYPOGRAPHY.paragraph2} className="text-mid-gray my-3">
              {cdl.has_ssn
                ? 'An SSN is securely stored. Its value is never shown here.'
                : 'The SSN is sent directly to secure storage and is needed only for new screenings.'}
            </TextV2>
            {tokenError && <Alert severity="error">{tokenError}</Alert>}
            {!locked && buttons('CDL', 'CDL information')}
          </fieldset>
        </form>
      )}
    </div>
  )
}

export function DriverEquipmentForm({ record, choices, run, busy }: FormProps) {
  const [truck, setTruck] = useOnboardingDraftState(record.truck || '')
  const done = record.status === 'ELIGIBLE'
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        void run(() => saveDriverOnboarding(record, { truck: truck || null }))
      }}
      className="space-y-3"
    >
      <TextField
        select
        label="Assigned truck"
        fullWidth
        disabled={done || busy}
        value={truck}
        onChange={(event) => setTruck(event.target.value)}
      >
        <MenuItem value="">Choose a truck</MenuItem>
        {choices.trucks
          .filter((item) => item.team_id === record.team.id)
          .map((item) => (
            <MenuItem value={item.id} key={item.id}>
              {item.vehicle_number || 'No unit number'} · {item.vin_number || 'No VIN'}
            </MenuItem>
          ))}
      </TextField>
      <TextV2 type={TYPOGRAPHY.paragraph2} className="text-mid-gray my-3">
        A truck with a unit number and complete VIN is required before eligibility. Add equipment to this team
        first if it is missing.
      </TextV2>
      {!done && (
        <Button type="submit" isDisabled={busy}>
          Save equipment
        </Button>
      )}
    </form>
  )
}
